import React, { Component } from "react";
import moment from "moment";
import * as Cookies from "es-cookie";
import { Row, Col, Modal } from "react-bootstrap";
import { components } from "react-select";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import TabSection from "./TabSection";
import SelectDropDown from "../../../../neetjee_guru/components/selectdropdown/SelectDropDown";
import { PageShimmer } from "./Shimmer";
import { pointTooltip } from "./chartTooltip";
import { examStreamsVar, examKeysVar, examStreamsKey } from './examStreams';

const DropdownIndicator = (props) =>
    components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
            <i className="fal fa-angle-down" />
        </components.DropdownIndicator>
    );

// Exam-paper list for the header dropdown (exam_paper_id + exam_name).
const GET_EXAM_PAPERS = gql`
  query ($student_user_exam_id: Int!, $exam_type: Int, $exam_streams: [Int], $exam_keys: [String], $fromDate: String, $toDate: String) {
    getStudentPerformanceReport(student_user_exam_id: $student_user_exam_id, exam_type: $exam_type, exam_streams: $exam_streams, exam_keys: $exam_keys, fromDate: $fromDate, toDate: $toDate) {
      examSubjectMatrix {
        exam_paper_id
        exam_name
        date
        has_time_data
        timed_questions
      }
    }
  }
`;

const GET_TIME_ANALYSIS = gql`
  query ($student_user_exam_id: Int!, $exam_type: String, $class_id: String, $exam_paper_id: Int, $exam_streams: [Int], $exam_keys: [String]) {
    getTimeAnalysisData(student_user_exam_id: $student_user_exam_id, exam_type: $exam_type, class_id: $class_id, exam_paper_id: $exam_paper_id, exam_streams: $exam_streams, exam_keys: $exam_keys) {
      paper_analysis {
        exam_paper_id
        total_questions
        answered_questions
        timed_questions
        has_time_data
        accuracy
        avg_time_per_question
        subject_breakdown {
          subject_id
          subject
          correct
          wrong
          not_answered
          accuracy
        }
        categories {
          key
          label
          time_range
          total_questions
          correct
          wrong
          accuracy
          avg_time_per_question
          questions {
            question_id
            subject_id
            status
            is_correct
            question_time
            original_time
            difficulty
            question
            option1
            option2
            option3
            option4
            answer
            attempt_answer
            explanation
            complexity
          }
        }
      }
    }
  }
`;

const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};

// Parse an exam date (handles common day/month/ISO orderings) into a sortable number.
const parseExamDate = (d) => {
    if (!d) return 0;
    const m = moment(d, ["DD-MM-YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601], true);
    return m.isValid() ? m.valueOf() : 0;
};

// Pick the exam paper with the latest date (falls back to the last array item).
const pickLatestExam = (matrix = []) => {
    if (!matrix || !matrix.length) return null;
    return matrix.reduce((best, e) => (parseExamDate(e.date) >= parseExamDate(best.date) ? e : best), matrix[0]);
};

// Format a time value (seconds) as "Hh Mm Ss", dropping leading zero units.
const formatSpeed = (value) => {
    const total = Math.round(parseFloat(value));
    if (isNaN(total) || total <= 0) return "0s";
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const parts = [];
    if (h) parts.push(`${h}h`);
    if (m) parts.push(`${m}m`);
    if (s || parts.length === 0) parts.push(`${s}s`);
    return parts.join(" ");
};

// Category accent palette keyed by label/key keyword (less / in / over time).
const catStyle = (cat) => {
    const l = `${cat.key || cat.label || ""}`.toLowerCase();
    if (l.includes("less")) return { color: "#0ea5e9", bg: "#e0f2fe", icon: "fas fa-bolt" };
    if (l.includes("over")) return { color: "#ef4444", bg: "#fee2e2", icon: "fas fa-hourglass-end" };
    return { color: "#16a34a", bg: "#dcfce7", icon: "fas fa-check-circle" }; // in time
};

const statusStyle = (q) => {
    // Prefer explicit is_correct, then status text.
    if (q.is_correct === true || q.is_correct === 1 || `${q.is_correct}` === "1")
        return { color: "#16a34a", bg: "#dcfce7", label: "Correct" };
    const s = `${q.status || ""}`.toLowerCase();
    if (s.includes("correct") && !s.includes("in")) return { color: "#16a34a", bg: "#dcfce7", label: "Correct" };
    if (s.includes("wrong") || s.includes("incorrect")) return { color: "#ef4444", bg: "#fee2e2", label: "Wrong" };
    if (s.includes("not") || s.includes("skip") || s.includes("unanswered"))
        return { color: "#64748b", bg: "#f1f5f9", label: "Not Answered" };
    if (q.is_correct === false || `${q.is_correct}` === "0") return { color: "#ef4444", bg: "#fee2e2", label: "Wrong" };
    return { color: "#64748b", bg: "#f1f5f9", label: q.status || "-" };
};

const diffStyle = (d) => {
    const s = `${d || ""}`.toLowerCase();
    if (s.includes("easy")) return { color: "#16a34a", bg: "#dcfce7" };
    if (s.includes("hard") || s.includes("difficult")) return { color: "#ef4444", bg: "#fee2e2" };
    if (s.includes("medium") || s.includes("moderate")) return { color: "#f59e0b", bg: "#fef3c7" };
    return { color: "#64748b", bg: "#f1f5f9" };
};

// MathJax config — inline/display latex delimiters used by the question bank.
const MATHJAX_CONFIG = {
    loader: { load: ["[tex]/html"] },
    tex: {
        inlineMath: [["$", "$"], ["\\(", "\\)"]],
        displayMath: [["$$", "$$"], ["\\[", "\\]"]],
    },
    svg: { fontCache: "global" },
};

// Render possibly-HTML content (questions/options carry markup & latex) via MathJax.
const HtmlBlock = ({ html, className, style, inline }) =>
    html ? (
        <MathJax dynamic inline={inline}>
            <span className={className} style={style} dangerouslySetInnerHTML={{ __html: html }} />
        </MathJax>
    ) : null;

const OPT_LETTERS = ["A", "B", "C", "D"];

// Map a numeric (or text) difficulty/complexity code to a readable label.
const levelLabel = (code) => {
    const s = `${code == null ? "" : code}`.trim();
    if (s === "1") return "Easy";
    if (s === "2") return "Moderate";
    if (s === "3") return "Difficult";
    if (s === "4") return "Highly Difficult";
    return s || "-";
};

// Does the correct/attempt marker point at this option index (1-based)?
// Backend stores answer/attempt_answer as the option LETTER ("A".."D") — the
// answer field may contain the letter within other text, so use includes().
const optionMatches = (marker, idx, optText) => {
    if (marker === null || marker === undefined || marker === "") return false;
    const m = `${marker}`.trim().toUpperCase();
    const letter = ["A", "B", "C", "D"][idx - 1];
    if (m.includes(letter)) return true; // "A", "Option A", "A,C"
    if (m === `${idx}`) return true; // numeric fallback "1".."4"
    if (optText && `${optText}`.trim() === `${marker}`.trim()) return true;
    return false;
};

// Resolve a correct/attempt marker to its option letter + text, if any.
const answerInfo = (marker, q) => {
    if (marker === null || marker === undefined || marker === "") return null;
    const opts = [q.option1, q.option2, q.option3, q.option4];
    for (let i = 0; i < 4; i++) {
        if (optionMatches(marker, i + 1, opts[i])) return { letter: OPT_LETTERS[i], text: opts[i] };
    }
    return { letter: `${marker}`, text: "" };
};

// Resolve the single option index (1-4) the student attempted, if identifiable.
const attemptIndexOf = (q) => {
    const marker = q.attempt_answer;
    if (marker === null || marker === undefined || `${marker}`.trim() === "") return null;
    const opts = [q.option1, q.option2, q.option3, q.option4];
    for (let i = 0; i < 4; i++) {
        if (optionMatches(marker, i + 1, opts[i])) return i + 1;
    }
    return null;
};

// Is a question a correct answer? (mirror statusStyle's correctness test)
const isQuestionCorrect = (q) =>
    q.is_correct === true ||
    q.is_correct === 1 ||
    `${q.is_correct}` === "1" ||
    (`${q.status || ""}`.toLowerCase().includes("correct") && !`${q.status || ""}`.toLowerCase().includes("incorrect"));

class TimeAnalysisTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            examPapers: [],
            selectedExamPaper: null,
            paperAnalysis: null,
            timeView: "graph", // "graph" | "table" — toggle for the time-band view
            modalCatIndex: null, // category index whose questions modal is open
            modalFilter: "all", // "all" | "correct" | "wrong" — questions shown in the modal
            papersLoading: true,
            dataLoading: false,
        };
    }

    componentDidMount() {
        this.fetchExamPapers();
    }

    // Re-fetch the (stream-scoped) exam-paper list when the header exam-stream
    // (Mains/Advanced) changes; that re-selects the latest paper and its analysis.
    componentDidUpdate(prevProps) {
        if (examStreamsKey(prevProps.examStreams) !== examStreamsKey(this.props.examStreams)) {
            this.fetchExamPapers();
        }
    }

    fetchExamPapers = async () => {
        this.setState({ papersLoading: true });
        try {
            const result = await this.props.client.query({
                query: GET_EXAM_PAPERS,
                variables: {
                    student_user_exam_id: parseInt(Cookies.get("student_user_exam_id")),
                    exam_type: 0,
                    exam_streams: examStreamsVar(this.props.examStreams),
                    exam_keys: examKeysVar(this.props.examKeys),
                    fromDate: "",
                    toDate: "",
                },
                fetchPolicy: "cache-first",
            });
            const matrix = result?.data?.getStudentPerformanceReport?.examSubjectMatrix || [];
            // "All exams (cumulative)" aggregates time-bands across every exam that
            // has time data (value -1 → backend aggregates for this student_user_exam_id). Offline exams
            // (no per-question time) are marked so the mix is clear in the dropdown.
            // One option per paper: a re-submitted exam has a matrix row per session.
            const uniqueMatrix = matrix.filter((m, i) => matrix.findIndex((x) => x.exam_paper_id === m.exam_paper_id) === i);
            const papers = [
                { value: -1, label: "All exams (cumulative)" },
                ...uniqueMatrix.map((m) => ({
                    value: m.exam_paper_id,
                    label: m.has_time_data ? m.exam_name : `${m.exam_name}  ·  offline (no time)`,
                    has_time_data: !!m.has_time_data,
                })),
            ];
            // Default to the cumulative view so the tab is immediately useful even
            // when the latest single exam happens to be offline (no time data).
            const defaultSel = papers[0];
            this.setState({ examPapers: papers, selectedExamPaper: defaultSel, papersLoading: false }, () => {
                this.fetchTimeAnalysis(defaultSel.value);
            });
        } catch (e) {
            console.log(e);
            this.setState({ examPapers: [], papersLoading: false });
        }
    };

    fetchTimeAnalysis = async (examPaperId) => {
        this.setState({ dataLoading: true, modalCatIndex: null, modalFilter: "all" });
        try {
            const result = await this.props.client.query({
                query: GET_TIME_ANALYSIS,
                variables: {
                    student_user_exam_id: parseInt(Cookies.get("student_user_exam_id")),
                    exam_type: "0",
                    class_id: "0",
                    exam_paper_id: parseInt(examPaperId) || 0,
                    exam_streams: examStreamsVar(this.props.examStreams),
                    exam_keys: examKeysVar(this.props.examKeys),
                },
                fetchPolicy: "cache-first",
            });
            const root = result?.data?.getTimeAnalysisData;
            const container = Array.isArray(root) ? root[0] : root;
            const analysis = container?.paper_analysis || (container && container.categories ? container : null) || null;
            this.setState({ paperAnalysis: analysis, dataLoading: false });
        } catch (e) {
            console.log(e);
            this.setState({ paperAnalysis: null, dataLoading: false });
        }
    };

    handleExamPaperChange = (name, value, selectedOption) => {
        this.setState({ selectedExamPaper: selectedOption }, () => {
            this.fetchTimeAnalysis(selectedOption?.value);
        });
    };

    renderControls = () => (
        <div className="d-flex flex-wrap align-items-center justify-content-end" style={{ gap: "12px" }}>
            {this.state.examPapers.length > 0 && (
                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <span className="font-weight-bold text-muted" style={{ fontSize: "13px" }}>Exam Paper</span>
                    <div style={{ minWidth: "220px" }}>
                        <SelectDropDown
                            stateData={this.state.selectedExamPaper}
                            handleChange={this.handleExamPaperChange}
                            name="selectedExamPaper"
                            options={this.state.examPapers}
                            placeholderName="Exam Paper"
                            dropdownIndicator={{ DropdownIndicator }}
                        />
                    </div>
                </div>
            )}
        </div>
    );

    renderSummary = (pa) => {
        const cards = [
            { label: "Total Questions", value: num(pa.total_questions), icon: "fas fa-list-ol", color: "#6366f1" },
            { label: "Answered", value: num(pa.answered_questions), icon: "fas fa-pen", color: "#8b5cf6" },
            { label: "Accuracy", value: `${num(pa.accuracy)}%`, icon: "fas fa-bullseye", color: "#16a34a" },
            { label: "Avg Time / Question", value: pa.has_time_data === false ? "—" : formatSpeed(pa.avg_time_per_question), icon: "far fa-clock", color: "#0ea5e9" },
        ];
        return (
            <Row className="mb-2">
                {cards.map((c, i) => (
                    <Col md={3} sm={6} key={i} className="mb-3">
                        <div className="d-flex align-items-center p-3" style={{ background: "#fff", border: "1px solid #eef0f4", borderRadius: 14, gap: 14, boxShadow: "0 4px 14px rgba(17,24,39,0.04)" }}>
                            <div className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 46, height: 46, borderRadius: 12, background: `${c.color}18`, color: c.color, fontSize: 18 }}>
                                <i className={c.icon} />
                            </div>
                            <div>
                                <div className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>{c.label}</div>
                                <div className="font-weight-bold text-dark" style={{ fontSize: 20 }}>{c.value}</div>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
        );
    };

    // Subject-wise correct/wrong breakdown — shown when there's no time data so
    // offline exams still give a meaningful accuracy view (not a dead end).
    renderSubjectBreakdown = (breakdown = []) => {
        if (!breakdown.length) return null;
        const cats = breakdown.map((b) => this.formatSubject(b.subject, b.subject_id));
        const options = {
            chart: { type: "column", backgroundColor: "transparent", height: 300 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: { categories: cats, lineColor: "#e2e8f0", labels: { style: { color: "#64748b", fontWeight: "600" } } },
            yAxis: [
                { min: 0, title: { text: "Questions", style: { color: "#94a3b8" } }, gridLineColor: "#eef2f6", labels: { style: { color: "#64748b" } } },
                { min: 0, max: 100, opposite: true, gridLineWidth: 0, title: { text: "Accuracy %", style: { color: "#16a34a" } }, labels: { format: "{value}%", style: { color: "#16a34a" } } },
            ],
            legend: { align: "center", verticalAlign: "top" },
            tooltip: pointTooltip({ unitBySeries: { "Accuracy %": "%" } }),
            plotOptions: { column: { borderRadius: 4, groupPadding: 0.14, dataLabels: { enabled: true, style: { fontWeight: "700", fontSize: "10.5px", textOutline: "none" } } } },
            series: [
                { name: "Correct", yAxis: 0, data: breakdown.map((b) => num(b.correct)), color: "#22c55e" },
                { name: "Wrong", yAxis: 0, data: breakdown.map((b) => num(b.wrong)), color: "#ef4444" },
                { name: "Not Answered", yAxis: 0, data: breakdown.map((b) => num(b.not_answered)), color: "#cbd5e1" },
                { name: "Accuracy %", type: "spline", yAxis: 1, data: breakdown.map((b) => num(b.accuracy)), color: "#16a34a", lineWidth: 3, marker: { enabled: true, radius: 5 }, tooltip: { valueSuffix: "%" } },
            ],
        };
        return (
            <>
                <HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { width: "100%" } }} />
                <Row className="mt-3">
                    {breakdown.map((b) => (
                        <Col md={4} sm={6} key={b.subject_id ?? b.subject} className="mb-3">
                            <div className="p-3 h-100" style={{ borderRadius: 14, border: "1px solid #f1f2f5", background: "#fafaff" }}>
                                <div className="font-weight-bold text-dark mb-2" style={{ fontSize: 14 }}>{this.formatSubject(b.subject, b.subject_id)}</div>
                                <div className="d-flex justify-content-between text-muted" style={{ fontSize: 12.5 }}><span>Correct</span><span className="font-weight-bold" style={{ color: "#16a34a" }}>{num(b.correct)}</span></div>
                                <div className="d-flex justify-content-between text-muted" style={{ fontSize: 12.5 }}><span>Wrong</span><span className="font-weight-bold" style={{ color: "#ef4444" }}>{num(b.wrong)}</span></div>
                                <div className="d-flex justify-content-between text-muted" style={{ fontSize: 12.5 }}><span>Not Answered</span><span className="font-weight-bold text-muted">{num(b.not_answered)}</span></div>
                                <div className="d-flex justify-content-between text-muted mt-1" style={{ fontSize: 12.5 }}><span>Accuracy</span><span className="font-weight-bold" style={{ color: "#16a34a" }}>{num(b.accuracy)}%</span></div>
                            </div>
                        </Col>
                    ))}
                </Row>
            </>
        );
    };

    // Subject label helper (falls back to id when name is missing).
    formatSubject = (name, id) => (name && String(name).trim() ? name : (id ? `Subject ${id}` : "Subject"));

    // Grouped column chart: Correct / Wrong per category + accuracy spline on 2nd axis.
    renderChart = (categories) => {
        const cats = categories.map((c) => c.label || c.key || "-");
        const correct = categories.map((c) => num(c.correct));
        const wrong = categories.map((c) => num(c.wrong));
        const unanswered = categories.map((c) => Math.max(0, num(c.total_questions) - num(c.correct) - num(c.wrong)));
        const accuracy = categories.map((c) => num(c.accuracy));

        const options = {
            chart: { backgroundColor: "transparent", height: 340 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: { categories: cats, lineColor: "#e2e8f0", labels: { style: { color: "#64748b", fontWeight: "600" } } },
            yAxis: [
                { title: { text: "" }, gridLineColor: "#eef2f6", labels: { style: { color: "#64748b" } } },
                { title: { text: "" }, opposite: true, max: 100, labels: { format: "{value}%", style: { color: "#64748b" } } },
            ],
            tooltip: pointTooltip({ unitBySeries: { "Accuracy %": "%" } }),
            legend: { align: "center", verticalAlign: "top" },
            plotOptions: {
                column: { borderRadius: 4, groupPadding: 0.12, dataLabels: { enabled: true, style: { fontWeight: "700", color: "#334155" } } },
                spline: { dataLabels: { enabled: true, format: "{point.y}%", style: { fontWeight: "700", color: "#6366f1" } } },
            },
            series: [
                { name: "Correct", type: "column", data: correct, color: "#16a34a" },
                { name: "Wrong", type: "column", data: wrong, color: "#ef4444" },
                { name: "Not Answered", type: "column", data: unanswered, color: "#cbd5e1" },
                { name: "Accuracy %", type: "spline", yAxis: 1, data: accuracy, color: "#6366f1", lineWidth: 3, marker: { radius: 4 } },
            ],
        };
        return (
            <div className="p-3 mb-3" style={{ background: "#fff", border: "1px solid #eef0f4", borderRadius: 14, boxShadow: "0 4px 14px rgba(17,24,39,0.04)" }}>
                <div className="font-weight-bold text-dark mb-2" style={{ fontSize: 15 }}>Time-band Performance</div>
                <HighchartsReact highcharts={Highcharts} options={options} />
            </div>
        );
    };

    // Open the questions modal for a category, optionally filtered by correctness.
    openModal = (i, filter = "all") => this.setState({ modalCatIndex: i, modalFilter: filter });

    // Category cards — click to open its questions modal.
    renderCategories = (categories) => (
        <Row>
            {categories.map((cat, i) => {
                const st = catStyle(cat);
                const count = num(cat.total_questions);
                return (
                    <Col md={4} key={i} className="mb-3">
                        <div
                            role="button"
                            onClick={() => count > 0 && this.openModal(i, "all")}
                            className="p-3 h-100"
                            style={{
                                background: "#fff",
                                border: `1.5px solid #eef0f4`,
                                borderRadius: 14,
                                cursor: count > 0 ? "pointer" : "default",
                                transition: "all .18s ease",
                                boxShadow: "0 4px 14px rgba(17,24,39,0.04)",
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="d-inline-flex align-items-center justify-content-center" style={{ width: 38, height: 38, borderRadius: 10, background: st.bg, color: st.color, fontSize: 16 }}>
                                    <i className={st.icon} />
                                </span>
                                {count > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: st.color }}>View <i className="fas fa-arrow-right ml-1" /></span>}
                            </div>
                            <div className="font-weight-bold text-dark" style={{ fontSize: 15 }}>{cat.label || "-"}</div>
                            <div className="text-muted mb-2" style={{ fontSize: 12 }}><i className="far fa-clock mr-1" />{cat.time_range || "-"}</div>
                            <div className="d-flex align-items-end" style={{ gap: 6 }}>
                                <span className="font-weight-bold" style={{ fontSize: 26, color: st.color, lineHeight: 1 }}>{count}</span>
                                <span className="text-muted mb-1" style={{ fontSize: 12 }}>questions</span>
                            </div>
                            <div className="d-flex mt-2 flex-wrap" style={{ gap: 12 }}>
                                <span
                                    role="button"
                                    onClick={(e) => { e.stopPropagation(); if (num(cat.correct) > 0) this.openModal(i, "correct"); }}
                                    style={{ fontSize: 12, fontWeight: 600, color: "#16a34a", cursor: num(cat.correct) > 0 ? "pointer" : "default", textDecoration: num(cat.correct) > 0 ? "underline" : "none" }}
                                    title="View correct questions"
                                >
                                    <i className="fas fa-check mr-1" />{num(cat.correct)} correct
                                </span>
                                <span
                                    role="button"
                                    onClick={(e) => { e.stopPropagation(); if (num(cat.wrong) > 0) this.openModal(i, "wrong"); }}
                                    style={{ fontSize: 12, fontWeight: 600, color: "#ef4444", cursor: num(cat.wrong) > 0 ? "pointer" : "default", textDecoration: num(cat.wrong) > 0 ? "underline" : "none" }}
                                    title="View wrong questions"
                                >
                                    <i className="fas fa-times mr-1" />{num(cat.wrong)} wrong
                                </span>
                            </div>
                            <div className="d-flex mt-1 flex-wrap" style={{ gap: 12 }}>
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    <i className="fas fa-bullseye mr-1" style={{ color: "#16a34a" }} />{num(cat.accuracy)}%
                                    {/* Show the sample so e.g. 100% on 3 questions reads honestly. */}
                                    {(num(cat.correct) + num(cat.wrong)) > 0 ? (
                                        <span style={{ color: "#94a3b8" }}> ({num(cat.correct)} of {num(cat.correct) + num(cat.wrong)})</span>
                                    ) : null}
                                </span>
                                <span className="text-muted" style={{ fontSize: 12 }}><i className="far fa-clock mr-1" style={{ color: "#0ea5e9" }} />{formatSpeed(cat.avg_time_per_question)}</span>
                            </div>
                        </div>
                    </Col>
                );
            })}
        </Row>
    );

    // Category summary table.
    renderCategoryTable = (categories) => (
        <div className="p-3 mb-3" style={{ background: "#fff", border: "1px solid #eef0f4", borderRadius: 14, boxShadow: "0 4px 14px rgba(17,24,39,0.04)" }}>
            <div className="font-weight-bold text-dark mb-2" style={{ fontSize: 15 }}>Category Summary</div>
            <div style={{ overflowX: "auto" }}>
                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: "#f8fafc" }}>
                            <th className="text-muted" style={{ fontWeight: 600, border: "none" }}>Category</th>
                            <th className="text-muted" style={{ fontWeight: 600, border: "none" }}>Time Range</th>
                            <th className="text-muted text-right" style={{ fontWeight: 600, border: "none" }}>Questions</th>
                            <th className="text-muted text-right" style={{ fontWeight: 600, border: "none" }}>Correct</th>
                            <th className="text-muted text-right" style={{ fontWeight: 600, border: "none" }}>Wrong</th>
                            <th className="text-muted text-right" style={{ fontWeight: 600, border: "none" }}>Accuracy</th>
                            <th className="text-muted text-right" style={{ fontWeight: 600, border: "none" }}>Avg Time</th>
                        </tr>
                    </thead>
                    <tbody>
                        {categories.map((cat, i) => {
                            const st = catStyle(cat);
                            return (
                                <tr key={i} role="button" style={{ cursor: num(cat.total_questions) > 0 ? "pointer" : "default" }} onClick={() => num(cat.total_questions) > 0 && this.openModal(i, "all")}>
                                    <td style={{ border: "none", verticalAlign: "middle" }}><span style={{ fontWeight: 700, color: st.color }}><i className={`${st.icon} mr-2`} />{cat.label || "-"}</span></td>
                                    <td className="text-muted" style={{ border: "none", verticalAlign: "middle" }}>{cat.time_range || "-"}</td>
                                    <td className="text-right font-weight-bold text-dark" style={{ border: "none", verticalAlign: "middle" }}>{num(cat.total_questions)}</td>
                                    <td className="text-right font-weight-bold" style={{ border: "none", verticalAlign: "middle", color: "#16a34a", cursor: num(cat.correct) > 0 ? "pointer" : "default", textDecoration: num(cat.correct) > 0 ? "underline" : "none" }} onClick={(e) => { e.stopPropagation(); if (num(cat.correct) > 0) this.openModal(i, "correct"); }}>{num(cat.correct)}</td>
                                    <td className="text-right font-weight-bold" style={{ border: "none", verticalAlign: "middle", color: "#ef4444", cursor: num(cat.wrong) > 0 ? "pointer" : "default", textDecoration: num(cat.wrong) > 0 ? "underline" : "none" }} onClick={(e) => { e.stopPropagation(); if (num(cat.wrong) > 0) this.openModal(i, "wrong"); }}>{num(cat.wrong)}</td>
                                    <td className="text-right text-dark" style={{ border: "none", verticalAlign: "middle" }}>{num(cat.accuracy)}%</td>
                                    <td className="text-right text-muted" style={{ border: "none", verticalAlign: "middle" }}>{formatSpeed(cat.avg_time_per_question)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );

    renderOption = (q, optText, idx, attemptIdx) => {
        if (optText === null || optText === undefined || optText === "") return null;
        const correctQ = isQuestionCorrect(q);
        // Correct questions: mark the correct answer green.
        // Wrong questions: mark the student's attempted option red.
        const showCorrect = correctQ && optionMatches(q.answer, idx, optText);
        const isAttempt = !correctQ && attemptIdx != null && idx === attemptIdx;
        let bg = "#fff", border = "#e9ecf2", badgeColor = "#94a3b8", tag = null;
        if (showCorrect) { bg = "#f0fdf4"; border = "#86efac"; badgeColor = "#16a34a"; tag = { text: "Correct Answer", color: "#16a34a" }; }
        if (isAttempt) { bg = "#fef2f2"; border = "#fca5a5"; badgeColor = "#ef4444"; tag = { text: "Your Answer", color: "#ef4444" }; }
        return (
            <div key={idx} className="d-flex align-items-start p-2 mb-2" style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, gap: 10 }}>
                <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0 font-weight-bold" style={{ width: 26, height: 26, borderRadius: 7, background: `${badgeColor}22`, color: badgeColor, fontSize: 12 }}>
                    {["A", "B", "C", "D"][idx - 1]}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <HtmlBlock html={optText} style={{ fontSize: 13, color: "#1f2937" }} />
                    {tag && <div style={{ fontSize: 10.5, fontWeight: 700, color: tag.color, marginTop: 2 }}>{tag.text}</div>}
                </div>
            </div>
        );
    };

    renderQuestionsModal = (categories) => {
        const i = this.state.modalCatIndex;
        if (i == null || !categories[i]) return null;
        const cat = categories[i];
        const st = catStyle(cat);
        // Less Time band → show the correct answer below each question.
        const isLessTime = `${cat.key || cat.label || ""}`.toLowerCase().includes("less");
        const filter = this.state.modalFilter;
        const allQuestions = cat.questions || [];
        // Filter by correctness when opened via the correct/wrong count.
        const questions =
            filter === "correct"
                ? allQuestions.filter((q) => isQuestionCorrect(q))
                : filter === "wrong"
                    ? allQuestions.filter((q) => !isQuestionCorrect(q))
                    : allQuestions;
        const filterMeta =
            filter === "correct"
                ? { label: "Correct only", color: "#16a34a", bg: "#dcfce7" }
                : filter === "wrong"
                    ? { label: "Wrong only", color: "#ef4444", bg: "#fee2e2" }
                    : null;
        return (
            <Modal show={true} onHide={() => this.setState({ modalCatIndex: null, modalFilter: "all" })} size="lg" centered dialogClassName="ta-question-modal">
                <Modal.Header closeButton style={{ borderBottom: "1px solid #eef0f4" }}>
                    <Modal.Title style={{ fontSize: 16 }}>
                        <span className="d-inline-flex align-items-center justify-content-center mr-2" style={{ width: 30, height: 30, borderRadius: 8, background: st.bg, color: st.color }}>
                            <i className={st.icon} style={{ fontSize: 13 }} />
                        </span>
                        <span className="font-weight-bold text-dark">{cat.label}</span>
                        {filterMeta && <span className="ml-2" style={{ fontSize: 11, fontWeight: 700, color: filterMeta.color, background: filterMeta.bg, borderRadius: 20, padding: "2px 10px" }}>{filterMeta.label}</span>}
                        <span className="text-muted ml-2" style={{ fontSize: 13 }}>{cat.time_range} · {questions.length} questions</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "72vh", overflowY: "auto", background: "#f8fafc" }}>
                  <MathJaxContext config={MATHJAX_CONFIG}>
                    {questions.length === 0 ? (
                        <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>No questions in this range.</div>
                    ) : (
                        questions.map((q, qi) => {
                            const ss = statusStyle(q);
                            const ds = diffStyle(q.difficulty);
                            // Wrong questions get a red accent, correct a green one.
                            const correctQ = isQuestionCorrect(q);
                            const attemptIdx = correctQ ? null : attemptIndexOf(q);
                            const accent = correctQ ? "#16a34a" : "#ef4444";
                            const cardBg = correctQ ? "#f6fef9" : "#fff7f7";
                            return (
                                <div key={q.question_id ?? qi} className="p-3 mb-3" style={{ background: cardBg, border: `1px solid ${correctQ ? "#c9f0da" : "#fbd5d5"}`, borderLeft: `4px solid ${accent}`, borderRadius: 14 }}>
                                    {/* meta row */}
                                    <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 8 }}>
                                        <span className="font-weight-bold text-dark" style={{ fontSize: 14 }}>Q{qi + 1}</span>
                                        <span className="text-muted" style={{ fontSize: 11.5 }}>#{q.question_id}</span>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: ss.color, background: ss.bg, borderRadius: 20, padding: "2px 10px" }}>{ss.label}</span>
                                        {q.difficulty != null && q.difficulty !== "" && <span style={{ fontSize: 11, fontWeight: 700, color: ds.color, background: ds.bg, borderRadius: 20, padding: "2px 10px" }}>Difficulty: {levelLabel(q.difficulty)}</span>}
                                        {q.complexity != null && q.complexity !== "" && <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", background: "#f1f5f9", borderRadius: 20, padding: "2px 10px" }}>Complexity: {levelLabel(q.complexity)}</span>}
                                        <span className="ml-auto text-muted" style={{ fontSize: 12 }}>
                                            <i className="far fa-clock mr-1" style={{ color: st.color }} />{formatSpeed(q.question_time)}
                                            <span className="mx-1">/</span>
                                            <span title="Ideal time">{formatSpeed(q.original_time)}</span>
                                        </span>
                                    </div>
                                    {/* question */}
                                    <HtmlBlock html={q.question} className="d-block mb-3" style={{ fontSize: 13.5, color: "#111827", fontWeight: 600 }} />
                                    {/* options */}
                                    {this.renderOption(q, q.option1, 1, attemptIdx)}
                                    {this.renderOption(q, q.option2, 2, attemptIdx)}
                                    {this.renderOption(q, q.option3, 3, attemptIdx)}
                                    {this.renderOption(q, q.option4, 4, attemptIdx)}
                                    {/* Less Time band → correct answer shown below */}
                                    {isLessTime && (() => {
                                        const ans = answerInfo(q.answer, q);
                                        return (
                                            <div className="mt-2 p-2 d-flex align-items-center" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, gap: 8 }}>
                                                <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0 font-weight-bold" style={{ width: 24, height: 24, borderRadius: 6, background: "#16a34a22", color: "#16a34a", fontSize: 12 }}>{ans ? ans.letter : "-"}</span>
                                                <div style={{ minWidth: 0 }}>
                                                    <div style={{ fontSize: 10.5, fontWeight: 700, color: "#16a34a" }}>CORRECT ANSWER</div>
                                                    {ans && ans.text ? <HtmlBlock html={ans.text} style={{ fontSize: 12.5, color: "#166534" }} /> : <span style={{ fontSize: 12.5, color: "#166534" }}>Option {ans ? ans.letter : "-"}</span>}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    {/* explanation */}
                                    {q.explanation && (
                                        <div className="mt-2 p-2" style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10 }}>
                                            <div className="font-weight-bold mb-1" style={{ fontSize: 12, color: "#2563eb" }}><i className="fas fa-lightbulb mr-1" />Explanation</div>
                                            <HtmlBlock html={q.explanation} style={{ fontSize: 12.5, color: "#334155" }} />
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                  </MathJaxContext>
                </Modal.Body>
            </Modal>
        );
    };

    render() {
        const { papersLoading, dataLoading, paperAnalysis, examPapers } = this.state;
        const categories = paperAnalysis?.categories || [];
        // No time data when the backend says so OR when no time band has any
        // questions (offline/OMR: every question_time is 0 → all bands empty).
        const noTimeData = paperAnalysis && (
            paperAnalysis.has_time_data === false ||
            (paperAnalysis.timed_questions != null && num(paperAnalysis.timed_questions) === 0) ||
            categories.every((c) => num(c.total_questions) === 0)
        );

        return (
            <TabSection
                icon="far fa-clock"
                title="Time Analysis"
                desc="Time spent versus accuracy across questions."
                headerRight={this.renderControls()}
            >
                {papersLoading ? (
                    <PageShimmer />
                ) : examPapers.length === 0 ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>No exam papers available.</div>
                ) : dataLoading ? (
                    <PageShimmer />
                ) : !paperAnalysis ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>No time analysis data for this exam paper.</div>
                ) : noTimeData ? (
                    // Offline / OMR exam — no per-question time, so the Less/In/Over
                    // time bands can't be built. Still show the correct/wrong
                    // breakdown (which DOES exist) plus a small note about timing,
                    // instead of a dead-end message.
                    <>
                        {this.renderSummary(paperAnalysis)}
                        <div className="mb-3 p-2 px-3" style={{ background: "#f8fafc", borderRadius: 10, fontSize: 12, color: "#64748b" }}>
                            <i className="far fa-clock mr-2" style={{ color: "#94a3b8" }} />
                            This was an offline / OMR test — per-question timing wasn't recorded, so the time-band
                            analysis isn't shown. The correct / wrong breakdown below is fully available.
                        </div>
                        {(paperAnalysis.subject_breakdown || []).length > 0 ? (
                            this.renderSubjectBreakdown(paperAnalysis.subject_breakdown)
                        ) : (
                            <div className="text-center text-muted py-4" style={{ fontSize: 13 }}>No question data for this exam.</div>
                        )}
                    </>
                ) : (
                    <>
                        {this.renderSummary(paperAnalysis)}
                        {/* Coverage transparency + Graph/Table toggle. */}
                        <div className="d-flex align-items-start justify-content-between flex-wrap mb-3" style={{ gap: 10 }}>
                            <div className="text-muted" style={{ fontSize: 12 }}>
                                {paperAnalysis.timed_questions != null && paperAnalysis.total_questions ? (
                                    <><i className="far fa-clock mr-1" />Time-band analysis based on {paperAnalysis.timed_questions} of {paperAnalysis.total_questions} timed questions</>
                                ) : null}
                            </div>
                            {categories.length > 0 && (
                                <div className="sp-segment sp-segment-sm">
                                    <button type="button" className={this.state.timeView === "graph" ? "active" : ""} onClick={() => this.setState({ timeView: "graph" })}>
                                        <i className="fas fa-chart-column" />Graph
                                    </button>
                                    <button type="button" className={this.state.timeView === "table" ? "active" : ""} onClick={() => this.setState({ timeView: "table" })}>
                                        <i className="fas fa-table" />Table
                                    </button>
                                </div>
                            )}
                        </div>
                        {categories.length > 0 && this.state.timeView === "graph" && this.renderChart(categories)}
                        {categories.length > 0 && this.state.timeView === "table" && this.renderCategoryTable(categories)}
                        {this.renderCategories(categories)}
                        {this.renderQuestionsModal(categories)}
                    </>
                )}
            </TabSection>
        );
    }
}

export default withRouter(compose(withApollo)(TimeAnalysisTab));
